/* eslint-disable react/jsx-no-constructed-context-values */
import { SelectOption } from 'new-components/MultiSelect'
import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { ZodIssue } from 'zod'

interface RegistrationContextProviderProps {
  children: ReactNode
}

// Interface para documento com URL do S3
export interface DocumentUploadData {
  type: string
  name_file: string
  extension: string
  url: string
}

const RegistrationContext = createContext({} as RegistrationContextValue)

export interface RegistrationContextValue {
  path?: RegistrationPath
  setPath: Dispatch<SetStateAction<RegistrationPath | undefined>>
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>

  typeOfBoard: string
  boardFu: string
  boardNumber: string
  hasPrescribedBefore: string
  typeOfBoardOther: string

  name: string
  nationality: string
  preferredName: string
  setPreferredName: Dispatch<SetStateAction<string>>
  genderIdentity: string
  gender: string
  cpf: string
  rg: string
  issuingAuthorityRg: string
  issuingAuthorityUfRg: string
  rne: string
  birthdate: string
  telephone: string
  setTelephone: Dispatch<SetStateAction<string>>
  cellphone: string
  specialties: string[]
  specialtiesRQE: string[]
  changeSpecialties: (newSpecialties: SelectOption[]) => void
  email: string
  emailConfirmation: string
  password: string
  passwordConfirmation: string

  // Campos de endereço
  addressCep: string
  address: string
  addressNumber: string
  addressComplement: string
  addressDistrict: string
  addressCity: string
  addressState: string

  // Plano de saúde
  healthPlan: string

  // Documentos (arquivos locais)
  identityDocument: File[]
  setIdentityDocument: Dispatch<SetStateAction<File[]>>
  prescription: File[]
  setPrescription: Dispatch<SetStateAction<File[]>>
  medicalReport: File[]
  setMedicalReport: Dispatch<SetStateAction<File[]>>
  lawsuitTerm: File[]
  setLawsuitTerm: Dispatch<SetStateAction<File[]>>

  // Documentos com URLs do S3
  uploadedDocuments: DocumentUploadData[]
  setUploadedDocuments: Dispatch<SetStateAction<DocumentUploadData[]>>
  addUploadedDocument: (document: DocumentUploadData) => void
  removeUploadedDocument: (type: string) => void

  removeFieldFromErrors: (fieldName: string) => void
  changeFieldValue: (fieldName: string, fieldValue: string | boolean) => void

  errors?: ZodIssue[]
  setErrors: Dispatch<SetStateAction<ZodIssue[] | undefined>>
}

export type RegistrationPath = 'patient' | 'professional'

export function RegistrationContextProvider({ children }: RegistrationContextProviderProps) {
  const [path, setPath] = useState<RegistrationPath>()
  const [loading, setLoading] = useState(false)

  // Validation form
  const [typeOfBoard, setTypeOfBoard] = useState('')
  const [boardFu, setBoardFu] = useState('')
  const [boardNumber, setBoardNumber] = useState('')
  const [hasPrescribedBefore, setHasPrescribedBefore] = useState('')
  const [typeOfBoardOther, setTypeOfBoardOther] = useState('')

  // Registration form
  const [name, setName] = useState('')
  const [nationality, setNationality] = useState('')
  const [preferredName, setPreferredName] = useState('')
  const [genderIdentity, setGenderIdentity] = useState('')
  const [gender, setGender] = useState('')
  const [cpf, setCpf] = useState('')
  const [rg, setRg] = useState('')
  const [issuingAuthorityRg, setIssuingAuthorityRg] = useState('')
  const [issuingAuthorityUfRg, setIssuingAuthorityUfRg] = useState('')
  const [rne, setRne] = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [telephone, setTelephone] = useState('')
  const [cellphone, setCellphone] = useState('')
  const [specialties, setSpecialties] = useState<string[]>([])
  const [specialtiesRQE, setSpecialtiesRQE] = useState<string[]>([])
  const [email, setEmail] = useState('')
  const [emailConfirmation, setEmailConfirmation] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')

  // Address fields
  const [addressCep, setAddressCep] = useState('')
  const [address, setAddress] = useState('')
  const [addressNumber, setAddressNumber] = useState('')
  const [addressComplement, setAddressComplement] = useState('')
  const [addressDistrict, setAddressDistrict] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressState, setAddressState] = useState('')

  // Health plan
  const [healthPlan, setHealthPlan] = useState('')

  // Document files
  const [identityDocument, setIdentityDocument] = useState<File[]>([])
  const [prescription, setPrescription] = useState<File[]>([])
  const [medicalReport, setMedicalReport] = useState<File[]>([])
  const [lawsuitTerm, setLawsuitTerm] = useState<File[]>([])

  // Documentos com URLs do S3
  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentUploadData[]>([])

  const addUploadedDocument = useCallback((document: DocumentUploadData) => {
    setUploadedDocuments((prev) => {
      // Remove documento existente do mesmo tipo antes de adicionar
      const filtered = prev.filter((doc) => doc.type !== document.type)
      return [...filtered, document]
    })
  }, [])

  const removeUploadedDocument = useCallback((type: string) => {
    setUploadedDocuments((prev) => prev.filter((doc) => doc.type !== type))
  }, [])

  // Validation
  const [errors, setErrors] = useState<ZodIssue[]>()

  const setFunctions = {
    typeOfBoard: setTypeOfBoard,
    boardFu: setBoardFu,
    boardNumber: setBoardNumber,
    hasPrescribedBefore: setHasPrescribedBefore,
    typeOfBoardOther: setTypeOfBoardOther,

    name: setName,
    nationality: setNationality,
    genderIdentity: setGenderIdentity,
    gender: setGender,
    cpf: setCpf,
    rg: setRg,
    issuingAuthorityRg: setIssuingAuthorityRg,
    issuingAuthorityUfRg: setIssuingAuthorityUfRg,
    rne: setRne,
    birthdate: setBirthdate,
    cellphone: setCellphone,
    specialties: setSpecialties,
    specialtiesRQE: setSpecialtiesRQE,
    email: setEmail,
    emailConfirmation: setEmailConfirmation,
    password: setPassword,
    passwordConfirmation: setPasswordConfirmation,

    addressCep: setAddressCep,
    address: setAddress,
    addressNumber: setAddressNumber,
    addressComplement: setAddressComplement,
    addressDistrict: setAddressDistrict,
    addressCity: setAddressCity,
    addressState: setAddressState,

    healthPlan: setHealthPlan
  }

  const removeFieldFromErrors = (fieldName: string) => {
    setErrors((prevErrors) => prevErrors?.filter((error) => !error.path.includes(fieldName)))
  }

  const changeFieldValue = useCallback(
    (fieldName: string, newValue: string | string[] | boolean) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const setFunction = setFunctions[fieldName]
      setFunction(newValue)
      if (errors) {
        removeFieldFromErrors(fieldName)
      }
    },
    [errors]
  )

  const changeSpecialties = useCallback(
    (newSpecialties: SelectOption[]) => {
      setSpecialties(newSpecialties.map((specialties) => specialties.value))
      setSpecialtiesRQE(newSpecialties.map((specialties) => specialties.rqe))
      if (errors) {
        removeFieldFromErrors('specialties')
      }
    },
    [errors]
  )

  useEffect(() => {
    setErrors(undefined)
  }, [path])

  return (
    <RegistrationContext.Provider
      value={{
        path,
        setPath,
        loading,
        setLoading,

        typeOfBoard,
        boardFu,
        boardNumber,
        hasPrescribedBefore,
        typeOfBoardOther,

        name,
        nationality,
        preferredName,
        setPreferredName,
        genderIdentity,
        gender,
        cpf,
        rg,
        issuingAuthorityRg,
        issuingAuthorityUfRg,
        rne,
        birthdate,
        telephone,
        setTelephone,
        cellphone,
        specialties,
        specialtiesRQE,
        changeSpecialties,
        email,
        emailConfirmation,
        password,
        passwordConfirmation,

        addressCep,
        address,
        addressNumber,
        addressComplement,
        addressDistrict,
        addressCity,
        addressState,

        healthPlan,

        identityDocument,
        setIdentityDocument,
        prescription,
        setPrescription,
        medicalReport,
        setMedicalReport,
        lawsuitTerm,
        setLawsuitTerm,

        uploadedDocuments,
        setUploadedDocuments,
        addUploadedDocument,
        removeUploadedDocument,

        changeFieldValue,
        removeFieldFromErrors,

        errors,
        setErrors
      }}>
      {children}
    </RegistrationContext.Provider>
  )
}

export default function useRegistration() {
  return useContext(RegistrationContext)
}
